import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, ChevronLeft, ChevronDown, X, Waves, Settings,
  ScrollText, Kanban, Building2, UserCircle, ShoppingBag, SlidersHorizontal,
  GitBranch, Paintbrush, Users, ShieldCheck, Layers, CheckSquare, Zap, Wifi,
  Settings2, BarChart2, TrendingUp, Target, ArrowDownCircle, MessageCircle, ClipboardList, Upload,
  Mail, FileText, Send, Shuffle, FileSignature, Receipt, Activity, Clock
} from "lucide-react";
import { cn } from "../lib/utils";
import { useAppearance } from "../contexts/AppearanceContext";
import { usePermissions } from "../hooks/usePermissions";
import { useModules } from "../contexts/ModulesContext";

const mainItemKeys = [
  { icon: LayoutDashboard, key: "nav.dashboard",  path: "/dashboard",               testId: "nav-dashboard-link" },
  { icon: Target,          key: "Leads",          path: "/leads",                   testId: "nav-leads-link", raw: true },
  { icon: Kanban,          key: "nav.negocios",   path: "/negocios",                testId: "nav-negocios-link" },
  { icon: Building2,       key: "nav.empresas",   path: "/empresas",                testId: "nav-empresas-link" },
  { icon: UserCircle,      key: "nav.contatos",   path: "/contatos",                testId: "nav-contatos-link" },
  { icon: ShoppingBag,     key: "nav.produtos",   path: "/produtos",                testId: "nav-produtos-link" },
  { icon: CheckSquare,     key: "nav.tarefas",    path: "/tarefas",                 testId: "nav-tarefas-link" },
  { icon: FileSignature,   key: "Contratos",      path: "/contratos",               testId: "nav-contratos-link", raw: true, requireIntegration: "autentique" },
  { icon: Receipt,         key: "Cobranças",      path: "/cobrancas",               testId: "nav-cobrancas-link", raw: true, requireIntegration: "asaas" },
  { icon: Zap,             key: "nav.automacoes", path: "/configuracoes/automacoes",testId: "nav-automacoes-link", badge: "BETA" },
];

const emailSubKeys = [
  { icon: FileText, key: "Modelos",   path: "/email/modelos",   testId: "nav-email-modelos-link", raw: true },
  { icon: Send,     key: "Campanhas", path: "/email/campanhas", testId: "nav-email-campanhas-link", raw: true },
];

const reportsSubKeys = [
  { icon: LayoutDashboard,  key: "Executivo",        path: "/relatorios/executive",  testId: "nav-rel-executive-link", raw: true },
  { icon: TrendingUp,      key: "nav.desempenho",  path: "/relatorios/vendas",    testId: "nav-rel-vendas-link" },
  { icon: Target,          key: "nav.forecast",    path: "/relatorios/forecast",  testId: "nav-rel-forecast-link" },
  { icon: ArrowDownCircle, key: "nav.conversao",   path: "/relatorios/conversao", testId: "nav-rel-conversao-link" },
  { icon: GitBranch,       key: "nav.funil",       path: "/relatorios/pipeline",  testId: "nav-rel-pipeline-link" },
  { icon: CheckSquare,     key: "nav.tarefas",     path: "/relatorios/tarefas",   testId: "nav-rel-tarefas-link" },
  { icon: MessageCircle,   key: "WhatsApp",        path: "/relatorios/whatsapp",  testId: "nav-rel-whatsapp-link", raw: true },
  { icon: Activity,        key: "Atividades",      path: "/relatorios/atividades", testId: "nav-rel-atividades-link", raw: true },
  { icon: Clock,           key: "Estagnados",      path: "/relatorios/aging",      testId: "nav-rel-aging-link", raw: true },
  { icon: Layers,          key: "Cohort",           path: "/relatorios/cohort",     testId: "nav-rel-cohort-link", raw: true },
];

const settingsSubKeys = [
  { icon: Settings2,         key: "Geral",             path: "/configuracoes/geral",       testId: "nav-geral-link", raw: true },
  { icon: Paintbrush,        key: "nav.aparencia",     path: "/configuracoes/aparencia",   testId: "nav-aparencia-link" },
  { icon: ScrollText,        key: "nav.auditoria",     path: "/configuracoes/auditoria",   testId: "nav-auditoria-link" },
  { icon: Layers,            key: "nav.modulos",       path: "/configuracoes/modulos",     testId: "nav-modulos-link" },
  { icon: GitBranch,         key: "nav.pipeline",      path: "/configuracoes/pipeline",    testId: "nav-pipeline-link" },
  { icon: SlidersHorizontal, key: "nav.campos",        path: "/configuracoes/campos",      testId: "nav-campos-link" },
  { icon: ClipboardList,     key: "Formulários",       path: "/configuracoes/formularios", testId: "nav-formularios-link", raw: true },
  { icon: Settings2,         key: "nav.tarefas",       path: "/configuracoes/tarefas",     testId: "nav-tarefas-config-link" },
  { icon: Users,             key: "nav.usuarios",      path: "/configuracoes/usuarios",    testId: "nav-usuarios-link" },
  { icon: ShieldCheck,       key: "nav.perfis",        path: "/configuracoes/perfis",      testId: "nav-perfis-link" },
  { icon: Shuffle,           key: "Robin Round",       path: "/configuracoes/robin-round", testId: "nav-robin-round-link", raw: true },
  { icon: Wifi,              key: "nav.integracoes",   path: "/configuracoes/integracoes", testId: "nav-integracoes-link" },
  { icon: Upload,            key: "import.title",      path: "/configuracoes/importar",    testId: "nav-importar-link" },
];

export default function Sidebar({ collapsed, onToggle, mobileOpen, onMobileClose }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const appearance = useAppearance();
  const { can, canConfig } = usePermissions();
  const { modules: moduleVisible } = useModules();

  // Build translated items at render time
  const [autentiqueConnected, setAutentiqueConnected] = useState(false);
  const [asaasConnected, setAsaasConnected] = useState(false);
  useEffect(() => {
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/integrations/autentique`).then(r => {
      setAutentiqueConnected(r.data?.enabled && !!r.data?.config?.api_key);
    }).catch(() => {});
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/integrations/asaas`).then(r => {
      setAsaasConnected(r.data?.enabled && !!r.data?.config?.api_key);
    }).catch(() => {});
  }, []);
  const mainItems = mainItemKeys
    .filter(i => {
      if (!i.requireIntegration) return true;
      if (i.requireIntegration === "autentique") return autentiqueConnected;
      if (i.requireIntegration === "asaas") return asaasConnected;
      return true;
    })
    .map(i => ({ ...i, label: i.raw ? i.key : t(i.key) }));
  const reportsSubItems = reportsSubKeys.map(i => ({ ...i, label: i.raw ? i.key : t(i.key) }));
  const settingsSubItems = settingsSubKeys.map(i => ({ ...i, label: i.raw ? i.key : t(i.key) }));
  const emailSubItems = emailSubKeys.map(i => ({ ...i, label: i.raw ? i.key : t(i.key) }));
  const [settingsOpen, setSettingsOpen] = useState(location.pathname.startsWith("/configuracoes") && location.pathname !== "/configuracoes/automacoes");
  const [reportsOpen, setReportsOpen] = useState(location.pathname.startsWith("/relatorios"));
  const [emailOpen, setEmailOpen] = useState(location.pathname.startsWith("/email"));
  const isSettingsActive = location.pathname.startsWith("/configuracoes") && location.pathname !== "/configuracoes/automacoes";
  const isReportsActive = location.pathname.startsWith("/relatorios");
  const isEmailActive = location.pathname.startsWith("/email");

  const isDark = appearance.sidebar_mode === "dark";
  const themeColor = appearance.colors?.["500"] || "#3B82F6";
  const themeColorLight = appearance.colors?.["50"] || "#EFF6FF";

  const sidebarBg = isDark ? "bg-slate-800" : "bg-gradient-to-b from-white to-[#eaeaea]";
  const sidebarBorder = isDark ? "border-slate-700" : "border-[#e0e0e0]";
  const logoTextColor = isDark ? "text-white" : "text-slate-800";
  const toggleColor = isDark ? "text-slate-400 hover:bg-slate-700" : "text-slate-400 hover:bg-slate-100";
  const separatorColor = isDark ? "border-slate-700" : "border-slate-200";
  const idleText = isDark ? "text-slate-400" : "text-slate-600";
  const idleIcon = isDark ? "text-slate-500" : "text-slate-400";
  const hoverBg = isDark ? "hover:bg-slate-700" : "hover:bg-slate-100";
  const subIdleText = isDark ? "text-slate-500" : "text-slate-500";
  const subActiveBg = isDark ? "bg-slate-700" : "bg-[#e7e7e7]";
  const subActiveText = isDark ? "text-white" : "text-slate-700";
  const subHoverBg = isDark ? "hover:bg-slate-700" : "hover:bg-slate-100";
  const subBorderColor = isDark ? "border-slate-600" : "border-slate-200";

  const NavContent = () => {
    return (<>
      <div className={cn("flex items-center border-b flex-shrink-0", separatorColor, collapsed ? "justify-center px-2 relative" : "px-4 relative")} style={{ height: "5rem" }}>
        {collapsed ? (
          <>
            {appearance.logo_url ? (
              <img src={appearance.logo_url} alt="Logo" className="w-9 h-9 object-contain" />
            ) : (
              <img src={isDark ? "/brand/icon-2.png" : "/brand/icon-1.png"} alt="Pipe4.me" className="w-9 h-9 object-contain" />
            )}
            <button data-testid="sidebar-toggle-btn" onClick={onToggle}
              className="absolute -right-9 top-1/2 -translate-y-1/2 w-7 h-10 bg-white rounded-full flex items-center justify-center shadow hover:bg-slate-50 transition-colors z-40">
              <ChevronLeft className="w-3.5 h-3.5 text-slate-400 rotate-180" />
            </button>
          </>
        ) : (
          <>
            <div className="flex items-center gap-3 flex-1 min-w-0">
              {appearance.logo_url ? (
                <div className="rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden" style={{ height: "3.5rem" }}>
                  <img src={appearance.logo_url} alt="Logo" className="max-h-full object-contain" />
                </div>
              ) : (
                <div className="flex items-center flex-shrink-0">
                  <img src={isDark ? "/brand/logo-2.png" : "/brand/logo-1.png"} alt="Pipe4.me" style={{ height: "3.5rem", objectFit: "contain" }} />
                </div>
              )}
            </div>
            <button data-testid="sidebar-toggle-btn" onClick={onToggle}
              className="absolute -right-9 top-1/2 -translate-y-1/2 w-7 h-10 bg-white rounded-full flex items-center justify-center shadow hover:bg-slate-50 transition-colors z-40">
              <ChevronLeft className="w-3.5 h-3.5 text-slate-400" />
            </button>
          </>
        )}
      </div>
      <nav className={cn("flex-1 py-4 space-y-1 overflow-y-auto", collapsed ? "px-2" : "px-3")}>
        {mainItems.filter(item => {
          const modMap = { "/dashboard": "dashboard", "/negocios": "negocios", "/leads": "leads", "/empresas": "empresas", "/contatos": "contatos", "/produtos": "produtos", "/tarefas": "tarefas", "/configuracoes/automacoes": "automacoes" };
          const mod = modMap[item.path];
          // Check form layout visibility for entity modules
          if (item.path === "/empresas" && !moduleVisible.empresas) return false;
          if (item.path === "/contatos" && !moduleVisible.contatos) return false;
          if (item.path === "/produtos" && !moduleVisible.produtos) return false;
          return !mod || can(mod, "view");
        }).map((item) => {
          const isActive = location.pathname === item.path || (item.path !== "/dashboard" && location.pathname.startsWith(item.path));
          return (
            <button key={item.path} data-testid={item.testId}
              onClick={() => { navigate(item.path); onMobileClose?.(); }}
              className={cn("w-full flex items-center gap-3 rounded-lg text-sm font-medium transition-all duration-150",
                collapsed ? "justify-center px-0 py-2.5" : "px-3 py-2.5",
                isActive ? "text-white" : `${idleText} ${hoverBg}`
              )}
              style={isActive ? { backgroundColor: themeColor } : undefined}
              title={collapsed ? item.label : undefined}>
              <item.icon className={cn("w-5 h-5 flex-shrink-0", isActive ? "text-white" : idleIcon)} strokeWidth={1.5} />
              {!collapsed && (
                <span className="flex items-center gap-2 flex-1">
                  {item.label}
                  {item.badge && (
                    <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full leading-none"
                      style={isActive ? { backgroundColor: "rgba(255,255,255,0.25)", color: "#fff" } : { backgroundColor: themeColor, color: "#fff" }}>
                      {item.badge}
                    </span>
                  )}
                </span>
              )}
            </button>
          );
        })}
        {/* E-mail submenu */}
        {can("email", "view") && (
        <div>
          <button data-testid="nav-email-toggle"
            onClick={() => { if (collapsed) { navigate("/email/modelos"); onMobileClose?.(); } else setEmailOpen(!emailOpen); }}
            className={cn("w-full flex items-center gap-3 rounded-lg text-sm font-medium transition-all duration-150",
              collapsed ? "justify-center px-0 py-2.5" : "px-3 py-2.5",
              isEmailActive ? "text-white" : `${idleText} ${hoverBg}`
            )}
            style={isEmailActive ? { backgroundColor: themeColor } : undefined}
            title={collapsed ? "E-mail" : undefined}>
            <Mail className={cn("w-5 h-5 flex-shrink-0", isEmailActive ? "text-white" : idleIcon)} strokeWidth={1.5} />
            {!collapsed && <>
              <span className="flex-1 text-left">E-mail</span>
              <ChevronDown className={cn("w-4 h-4 transition-transform duration-200", isDark ? "text-slate-500" : "text-slate-300", emailOpen && "rotate-180")} />
            </>}
          </button>
          {!collapsed && emailOpen && (
            <div className={cn("mt-1 ml-4 pl-3 border-l-2 space-y-1", subBorderColor)}>
              {emailSubItems.map(sub => {
                const isSubActive = location.pathname === sub.path;
                return (
                  <button key={sub.path} data-testid={sub.testId}
                    onClick={() => { navigate(sub.path); onMobileClose?.(); }}
                    className={cn("w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-all duration-150 overflow-hidden",
                      isSubActive ? `${subActiveBg} ${subActiveText} font-medium` : `${subIdleText} ${subHoverBg}`
                    )}>
                    <sub.icon className={cn("w-4 h-4 flex-shrink-0", isSubActive ? subActiveText : isDark ? "text-slate-500" : "text-slate-400")} strokeWidth={1.5} />
                    <span className="truncate flex-1 text-left">{sub.label}</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
        )}
        {/* Relatórios submenu */}
        {can("relatorios", "view") && (
        <div>
          <button data-testid="nav-relatorios-toggle"
            onClick={() => { if (collapsed) { navigate("/relatorios/vendas"); onMobileClose?.(); } else setReportsOpen(!reportsOpen); }}
            className={cn("w-full flex items-center gap-3 rounded-lg text-sm font-medium transition-all duration-150",
              collapsed ? "justify-center px-0 py-2.5" : "px-3 py-2.5",
              isReportsActive ? "text-white" : `${idleText} ${hoverBg}`
            )}
            style={isReportsActive ? { backgroundColor: themeColor } : undefined}
            title={collapsed ? t("nav.relatorios") : undefined}>
            <BarChart2 className={cn("w-5 h-5 flex-shrink-0", isReportsActive ? "text-white" : idleIcon)} strokeWidth={1.5} />
            {!collapsed && <>
              <span className="flex-1 text-left">{t("nav.relatorios")}</span>
              <ChevronDown className={cn("w-4 h-4 transition-transform duration-200", isDark ? "text-slate-500" : "text-slate-300", reportsOpen && "rotate-180")} />
            </>}
          </button>
          {!collapsed && reportsOpen && (
            <div className={cn("mt-1 ml-4 pl-3 border-l-2 space-y-1", subBorderColor)}>
              {reportsSubItems.map(sub => {
                const isSubActive = location.pathname === sub.path;
                return (
                  <button key={sub.path} data-testid={sub.testId}
                    onClick={() => { navigate(sub.path); onMobileClose?.(); }}
                    className={cn("w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-all duration-150 overflow-hidden",
                      isSubActive ? `${subActiveBg} ${subActiveText} font-medium` : `${subIdleText} ${subHoverBg}`
                    )}>
                    <sub.icon className={cn("w-4 h-4 flex-shrink-0", isSubActive ? subActiveText : isDark ? "text-slate-500" : "text-slate-400")} strokeWidth={1.5} />
                    <span className="truncate flex-1 text-left">{sub.label}</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
        )}
        <div className={cn("my-3 border-t", separatorColor)} />
        {(() => {
          const cfgMap = { "/configuracoes/campos": "campos", "/configuracoes/pipeline": "pipeline", "/configuracoes/perfis": "perfis", "/configuracoes/usuarios": "usuarios", "/configuracoes/aparencia": "aparencia", "/configuracoes/auditoria": "auditoria", "/configuracoes/geral": "geral", "/configuracoes/modulos": "modulos", "/configuracoes/formularios": "formularios", "/configuracoes/tarefas": "tarefas_config", "/configuracoes/integracoes": "integracoes", "/configuracoes/robin-round": "robin_round", "/configuracoes/importar": "importar" };
          const visibleSubs = settingsSubItems.filter(sub => { const s = cfgMap[sub.path]; return !s || canConfig(s); });
          if (visibleSubs.length === 0) return null;
          return (
        <div>
          <button data-testid="nav-configuracoes-toggle"
            onClick={() => { if (collapsed) { navigate(visibleSubs[0].path); onMobileClose?.(); } else setSettingsOpen(!settingsOpen); }}
            className={cn("w-full flex items-center gap-3 rounded-lg text-sm font-medium transition-all duration-150",
              collapsed ? "justify-center px-0 py-2.5" : "px-3 py-2.5",
              isSettingsActive ? "text-white" : `${idleText} ${hoverBg}`
            )}
            style={isSettingsActive ? { backgroundColor: themeColor } : undefined}
            title={collapsed ? t("nav.configuracoes") : undefined}>
            <Settings className={cn("w-5 h-5 flex-shrink-0", isSettingsActive ? "text-white" : idleIcon)} strokeWidth={1.5} />
            {!collapsed && <>
              <span className="flex-1 text-left">{t("nav.configuracoes")}</span>
              <ChevronDown className={cn("w-4 h-4 transition-transform duration-200", isDark ? "text-slate-500" : "text-slate-300", settingsOpen && "rotate-180")} />
            </>}
          </button>
          {!collapsed && settingsOpen && (
            <div className={cn("mt-1 ml-4 pl-3 border-l-2 space-y-1", subBorderColor)}>
              {visibleSubs.map((sub) => {
                const isSubActive = location.pathname === sub.path ||
                  (sub.path !== "/configuracoes" && location.pathname.startsWith(sub.path + "/"));
                return (
                  <button key={sub.path} data-testid={sub.testId}
                    onClick={() => { navigate(sub.path); onMobileClose?.(); }}
                    className={cn("w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-all duration-150",
                      isSubActive ? `${subActiveBg} ${subActiveText} font-medium` : `${subIdleText} ${subHoverBg}`
                    )}>
                    <sub.icon className={cn("w-4 h-4 flex-shrink-0", isSubActive ? subActiveText : isDark ? "text-slate-500" : "text-slate-400")} strokeWidth={1.5} />
                    <span className="truncate flex-1 text-left">{sub.label}</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
          );
        })()}
      </nav>
    </>);
  };

  return (
    <>
      <aside data-testid="desktop-sidebar" className={cn(
        "hidden lg:flex flex-col fixed left-0 top-0 h-screen border-r z-30 transition-all duration-200",
        sidebarBg, sidebarBorder, collapsed ? "w-16" : "w-64"
      )}><div className="flex flex-col h-full">{NavContent()}</div></aside>
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={onMobileClose} />
          <aside className={cn("absolute left-0 top-0 h-full w-64 shadow-xl border-r", sidebarBg, sidebarBorder)}>
            <div className="absolute top-4 right-4">
              <button data-testid="mobile-sidebar-close-btn" onClick={onMobileClose}
                className={cn("w-8 h-8 flex items-center justify-center rounded-md", toggleColor)}>
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex flex-col h-full">{NavContent()}</div>
          </aside>
        </div>
      )}
    </>
  );
}
